// Daily Quiz Questions Bank - 100+ Questions
// Questions are categorized and will be randomly selected each day

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
}

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  // ==================== PRODUCTIVITY (30 questions) ====================
  {
    id: 'prod_001',
    question: 'What is the Pomodoro Technique?',
    options: [
      'A cooking method',
      'A time management method with 25-minute work intervals',
      'A type of tomato',
      'A productivity app'
    ],
    correctAnswer: 1,
    explanation: 'The Pomodoro Technique uses 25-minute focused work sessions followed by short breaks to maintain productivity and prevent burnout.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_002',
    question: 'What does "Eat the Frog" mean in productivity?',
    options: [
      'Have a healthy breakfast',
      'Do your hardest task first thing in the morning',
      'Avoid difficult tasks',
      'Take breaks frequently'
    ],
    correctAnswer: 1,
    explanation: '"Eat the Frog" means tackling your most challenging or important task first thing in the morning when your energy is highest.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_003',
    question: 'What is the 80/20 rule (Pareto Principle)?',
    options: [
      'Work 80 hours, rest 20 hours',
      '80% of results come from 20% of efforts',
      'Spend 80% on work, 20% on breaks',
      'Complete 80% of tasks, delegate 20%'
    ],
    correctAnswer: 1,
    explanation: 'The Pareto Principle states that 80% of outcomes come from 20% of causes. Focus on the vital few tasks that create the most impact.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_004',
    question: 'What is "Deep Work"?',
    options: [
      'Working underwater',
      'Working late at night',
      'Focused work without distractions',
      'Working on complex problems'
    ],
    correctAnswer: 2,
    explanation: 'Deep Work is the ability to focus without distraction on cognitively demanding tasks, producing high-quality work efficiently.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_005',
    question: 'What is the best time to do your most important work?',
    options: [
      'Late at night',
      'During your peak energy hours',
      'Right after lunch',
      'Whenever you have free time'
    ],
    correctAnswer: 1,
    explanation: 'Schedule your most important work during your peak energy hours when you\'re most alert and focused (often morning for most people).',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_006',
    question: 'What is "Batch Processing" in productivity?',
    options: [
      'Cooking in large quantities',
      'Grouping similar tasks together to do at once',
      'Processing documents in batches',
      'Working in groups'
    ],
    correctAnswer: 1,
    explanation: 'Batch processing means grouping similar tasks together and completing them in one focused session, reducing context switching and increasing efficiency.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_007',
    question: 'What is the Eisenhower Matrix used for?',
    options: [
      'Mathematical calculations',
      'Prioritizing tasks by urgency and importance',
      'Team organization',
      'Time zone conversions'
    ],
    correctAnswer: 1,
    explanation: 'The Eisenhower Matrix helps prioritize tasks by categorizing them as Urgent/Important, Not Urgent/Important, Urgent/Not Important, or Neither.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_008',
    question: 'What should you do with tasks that are neither urgent nor important?',
    options: [
      'Do them first',
      'Delegate or eliminate them',
      'Schedule them for tomorrow',
      'Add them to your priority list'
    ],
    correctAnswer: 1,
    explanation: 'Tasks that are neither urgent nor important should be delegated to others or eliminated entirely to focus on what truly matters.',
    category: 'Productivity',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'prod_009',
    question: 'What is "Time Blocking"?',
    options: [
      'Blocking distracting websites',
      'Scheduling specific time slots for specific tasks',
      'Working without a clock',
      'Blocking out noise while working'
    ],
    correctAnswer: 1,
    explanation: 'Time blocking is scheduling specific blocks of time for specific tasks or activities, helping you stay focused and organized.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_010',
    question: 'What is the "Two-Minute Rule"?',
    options: [
      'Take a 2-minute break every hour',
      'If a task takes less than 2 minutes, do it immediately',
      'Spend only 2 minutes on each task',
      'Make decisions in 2 minutes'
    ],
    correctAnswer: 1,
    explanation: 'The Two-Minute Rule states that if a task takes less than two minutes to complete, do it immediately rather than adding it to your to-do list.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_011',
    question: 'What is "Context Switching"?',
    options: [
      'Changing your work environment',
      'Switching between different tasks or projects',
      'Changing your mindset',
      'Switching between devices'
    ],
    correctAnswer: 1,
    explanation: 'Context switching is moving between different tasks or projects, which reduces productivity as your brain needs time to refocus.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_012',
    question: 'What is the purpose of a "Morning Routine"?',
    options: [
      'To wake up early',
      'To set a positive tone and prepare mentally for the day',
      'To exercise',
      'To eat breakfast'
    ],
    correctAnswer: 1,
    explanation: 'A morning routine helps set a positive tone, prepare mentally, and establish momentum for a productive day ahead.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_013',
    question: 'What does "MIT" stand for in productivity?',
    options: [
      'Massachusetts Institute of Technology',
      'Most Important Task',
      'Morning Intensive Training',
      'Multiple Item Tracking'
    ],
    correctAnswer: 1,
    explanation: 'MIT stands for "Most Important Task" - the one task that will have the biggest impact on your day if completed.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_014',
    question: 'How long does it take to form a new habit?',
    options: [
      '7 days',
      '21 days',
      '66 days on average',
      '100 days'
    ],
    correctAnswer: 2,
    explanation: 'Research shows it takes an average of 66 days for a new behavior to become automatic, though it varies by person and habit complexity.',
    category: 'Productivity',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'prod_015',
    question: 'What is "Decision Fatigue"?',
    options: [
      'Being tired of making decisions',
      'The deteriorating quality of decisions after making many decisions',
      'Avoiding making decisions',
      'Making decisions too quickly'
    ],
    correctAnswer: 1,
    explanation: 'Decision fatigue occurs when the quality of your decisions deteriorates after making many decisions, which is why it\'s best to make important decisions early in the day.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_016',
    question: 'What is the best way to handle interruptions at work?',
    options: [
      'Ignore everyone',
      'Set specific "office hours" for questions and close door during focus time',
      'Always be available',
      'Work from home'
    ],
    correctAnswer: 1,
    explanation: 'Setting specific times for availability and protecting focus time helps balance accessibility with productivity.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_017',
    question: 'What is "Timeboxing"?',
    options: [
      'Putting tasks in boxes',
      'Allocating a fixed time period to an activity',
      'Working in a box',
      'Tracking time spent'
    ],
    correctAnswer: 1,
    explanation: 'Timeboxing is allocating a fixed, maximum unit of time for an activity, helping prevent perfectionism and maintain progress.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prod_018',
    question: 'What percentage of your work time should be spent on planning?',
    options: [
      '5-10%',
      '15-20%',
      '30-40%',
      '50%'
    ],
    correctAnswer: 1,
    explanation: 'Spending 15-20% of your time on planning helps ensure you\'re working on the right things and working efficiently.',
    category: 'Productivity',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'prod_019',
    question: 'What is the "Getting Things Done" (GTD) method?',
    options: [
      'Working faster',
      'A productivity system for capturing and organizing tasks',
      'Delegating all work',
      'Working longer hours'
    ],
    correctAnswer: 1,
    explanation: 'GTD is a comprehensive productivity system that helps capture, clarify, organize, and review all your commitments and tasks.',
    category: 'Productivity',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prod_020',
    question: 'What is "Single-Tasking"?',
    options: [
      'Working alone',
      'Focusing on one task at a time',
      'Having only one task per day',
      'Delegating all but one task'
    ],
    correctAnswer: 1,
    explanation: 'Single-tasking means focusing on one task at a time, which is more efficient than multitasking and produces better quality work.',
    category: 'Productivity',
    difficulty: 'easy',
    points: 10
  },

  // ==================== COMMUNICATION (25 questions) ====================
  {
    id: 'comm_001',
    question: 'What is the most effective way to communicate urgent issues?',
    options: [
      'Send an email',
      'Direct call or face-to-face',
      'Post on social media',
      'Wait for the next meeting'
    ],
    correctAnswer: 1,
    explanation: 'For urgent issues, direct communication (call or face-to-face) ensures immediate attention and quick resolution.',
    category: 'Communication',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'comm_002',
    question: 'What is "Active Listening"?',
    options: [
      'Listening to music while working',
      'Fully concentrating and responding to what is being said',
      'Listening while multitasking',
      'Listening to podcasts'
    ],
    correctAnswer: 1,
    explanation: 'Active listening means fully concentrating, understanding, responding, and remembering what is being said without distractions.',
    category: 'Communication',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'comm_003',
    question: 'What is the ideal length for a productive meeting?',
    options: [
      '15-30 minutes',
      '45-60 minutes',
      '2 hours',
      'As long as needed'
    ],
    correctAnswer: 0,
    explanation: 'Research shows that 15-30 minute meetings are most productive, keeping participants focused and engaged without fatigue.',
    category: 'Communication',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'comm_004',
    question: 'What is "Constructive Feedback"?',
    options: [
      'Only positive comments',
      'Specific, actionable suggestions for improvement',
      'Criticism without solutions',
      'Comparing with others'
    ],
    correctAnswer: 1,
    explanation: 'Constructive feedback provides specific, actionable suggestions for improvement while maintaining respect and focusing on behaviors, not personality.',
    category: 'Communication',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'comm_005',
    question: 'What is the "SBI Model" for giving feedback?',
    options: [
      'Simple, Brief, Immediate',
      'Situation, Behavior, Impact',
      'Specific, Balanced, Informative',
      'Start, Build, Implement'
    ],
    correctAnswer: 1,
    explanation: 'The SBI Model (Situation, Behavior, Impact) helps structure feedback by describing the situation, the specific behavior, and its impact.',
    category: 'Communication',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'comm_006',
    question: 'What percentage of communication is non-verbal?',
    options: [
      '20-30%',
      '40-50%',
      '55-65%',
      '80-90%'
    ],
    correctAnswer: 2,
    explanation: 'Studies show that 55-65% of communication is non-verbal (body language, facial expressions, tone), making in-person communication powerful.',
    category: 'Communication',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'comm_007',
    question: 'What is the best way to handle a conflict with a colleague?',
    options: [
      'Avoid them',
      'Address it privately and directly with respect',
      'Complain to others',
      'Send an angry email'
    ],
    correctAnswer: 1,
    explanation: 'Addressing conflicts privately and directly, with respect and focus on solutions, is the most professional and effective approach.',
    category: 'Communication',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'comm_008',
    question: 'What does "CC" mean in email?',
    options: [
      'Copy and Create',
      'Carbon Copy - send copy to additional recipients',
      'Confirm and Close',
      'Critical Communication'
    ],
    correctAnswer: 1,
    explanation: 'CC (Carbon Copy) sends a copy of the email to additional recipients who should be informed but aren\'t the primary audience.',
    category: 'Communication',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'comm_009',
    question: 'When should you use "Reply All" in email?',
    options: [
      'Always',
      'Only when your response is relevant to everyone',
      'Never',
      'When you want attention'
    ],
    correctAnswer: 1,
    explanation: 'Use "Reply All" only when your response is relevant and valuable to everyone on the thread to avoid cluttering inboxes.',
    category: 'Communication',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'comm_010',
    question: 'What is "Emotional Intelligence" (EQ)?',
    options: [
      'Being emotional at work',
      'The ability to understand and manage emotions',
      'Intelligence about emotions',
      'Avoiding emotions'
    ],
    correctAnswer: 1,
    explanation: 'Emotional Intelligence is the ability to recognize, understand, and manage your own emotions and those of others, crucial for workplace success.',
    category: 'Communication',
    difficulty: 'medium',
    points: 15
  },

  // ==================== WELLNESS & WORK-LIFE BALANCE (20 questions) ====================
  {
    id: 'well_001',
    question: 'How often should you take breaks during work?',
    options: [
      'Never, stay focused',
      'Every 25-50 minutes',
      'Only at lunch',
      'Whenever you feel tired'
    ],
    correctAnswer: 1,
    explanation: 'Regular breaks every 25-50 minutes help maintain focus, prevent burnout, and increase overall productivity throughout the day.',
    category: 'Wellness',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'well_002',
    question: 'What is the recommended amount of sleep for optimal productivity?',
    options: [
      '4-5 hours',
      '6-7 hours',
      '7-9 hours',
      '10+ hours'
    ],
    correctAnswer: 2,
    explanation: 'Most adults need 7-9 hours of quality sleep for optimal cognitive function, focus, memory, and productivity.',
    category: 'Wellness',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'well_003',
    question: 'What is the best way to handle work stress?',
    options: [
      'Ignore it and keep working',
      'Take regular breaks, exercise, and communicate',
      'Work longer hours',
      'Drink more coffee'
    ],
    correctAnswer: 1,
    explanation: 'Managing stress requires regular breaks, physical activity, open communication with colleagues/managers, and maintaining work-life balance.',
    category: 'Wellness',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'well_004',
    question: 'What is "Burnout"?',
    options: [
      'Working too fast',
      'Physical and emotional exhaustion from chronic workplace stress',
      'Getting fired',
      'Working overtime'
    ],
    correctAnswer: 1,
    explanation: 'Burnout is a state of physical and emotional exhaustion caused by prolonged workplace stress, characterized by cynicism and reduced effectiveness.',
    category: 'Wellness',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'well_005',
    question: 'How much water should you drink during a workday?',
    options: [
      '1-2 glasses',
      '4-6 glasses',
      '8-10 glasses',
      'Only when thirsty'
    ],
    correctAnswer: 2,
    explanation: 'Drinking 8-10 glasses (about 2 liters) of water during the workday helps maintain focus, energy, and overall health.',
    category: 'Wellness',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'well_006',
    question: 'What is the "20-20-20 Rule" for eye health?',
    options: [
      'Work 20 hours, rest 20 hours',
      'Every 20 minutes, look at something 20 feet away for 20 seconds',
      'Blink 20 times every 20 minutes',
      'Take 20-minute breaks every 20 hours'
    ],
    correctAnswer: 1,
    explanation: 'The 20-20-20 rule helps prevent eye strain: every 20 minutes, look at something 20 feet away for at least 20 seconds.',
    category: 'Wellness',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'well_007',
    question: 'What is the ideal posture for desk work?',
    options: [
      'Slouched and relaxed',
      'Feet flat, back straight, screen at eye level',
      'Leaning forward',
      'Standing all day'
    ],
    correctAnswer: 1,
    explanation: 'Proper ergonomics include feet flat on floor, back straight with lumbar support, and screen at eye level to prevent strain.',
    category: 'Wellness',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'well_008',
    question: 'What is "Mindfulness" in the workplace?',
    options: [
      'Being mindful of deadlines',
      'Being present and aware in the current moment',
      'Thinking about work constantly',
      'Planning ahead'
    ],
    correctAnswer: 1,
    explanation: 'Mindfulness is being fully present and aware in the current moment, which reduces stress and improves focus and decision-making.',
    category: 'Wellness',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'well_009',
    question: 'How often should you move or stretch during desk work?',
    options: [
      'Once a day',
      'Every 30-60 minutes',
      'Only when you feel stiff',
      'During lunch only'
    ],
    correctAnswer: 1,
    explanation: 'Moving or stretching every 30-60 minutes prevents muscle tension, improves circulation, and maintains energy levels.',
    category: 'Wellness',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'well_010',
    question: 'What is "Work-Life Integration"?',
    options: [
      'Working from home',
      'Blending work and personal life flexibly',
      'Working all the time',
      'Separating work and life completely'
    ],
    correctAnswer: 1,
    explanation: 'Work-life integration is flexibly blending work and personal activities throughout the day, rather than strict separation.',
    category: 'Wellness',
    difficulty: 'medium',
    points: 15
  },

  // ==================== GOAL SETTING & PLANNING (15 questions) ====================
  {
    id: 'goal_001',
    question: 'What does SMART stand for in goal setting?',
    options: [
      'Simple, Measurable, Achievable, Realistic, Timely',
      'Specific, Measurable, Achievable, Relevant, Time-bound',
      'Strategic, Meaningful, Actionable, Realistic, Trackable',
      'Simple, Meaningful, Accurate, Relevant, Timely'
    ],
    correctAnswer: 1,
    explanation: 'SMART goals are Specific, Measurable, Achievable, Relevant, and Time-bound - a proven framework for effective goal setting.',
    category: 'Goal Setting',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'goal_002',
    question: 'What is the difference between a goal and a task?',
    options: [
      'They are the same thing',
      'A goal is the desired outcome, a task is an action to achieve it',
      'A goal is smaller than a task',
      'A task is more important'
    ],
    correctAnswer: 1,
    explanation: 'A goal is the desired end result or outcome, while a task is a specific action or step taken to achieve that goal.',
    category: 'Goal Setting',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'goal_003',
    question: 'How often should you review your goals?',
    options: [
      'Once a year',
      'Weekly or monthly',
      'Never, just set and forget',
      'Only when you fail'
    ],
    correctAnswer: 1,
    explanation: 'Regular goal review (weekly or monthly) helps track progress, adjust strategies, and stay motivated and on track.',
    category: 'Goal Setting',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'goal_004',
    question: 'What is a "Stretch Goal"?',
    options: [
      'A goal related to exercise',
      'An ambitious goal that pushes beyond comfort zone',
      'An easy goal',
      'A flexible goal'
    ],
    correctAnswer: 1,
    explanation: 'A stretch goal is an ambitious target that pushes you beyond your comfort zone, driving innovation and exceptional performance.',
    category: 'Goal Setting',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'goal_005',
    question: 'What is "Backward Planning"?',
    options: [
      'Planning in reverse order',
      'Starting from the goal and working backward to identify steps',
      'Planning for the past',
      'Undoing plans'
    ],
    correctAnswer: 1,
    explanation: 'Backward planning starts with the end goal and works backward to identify all necessary steps and milestones to achieve it.',
    category: 'Goal Setting',
    difficulty: 'medium',
    points: 15
  },

  // ==================== TEAMWORK & COLLABORATION (15 questions) ====================
  {
    id: 'team_001',
    question: 'What makes a team effective?',
    options: [
      'Having the most members',
      'Clear goals, good communication, and mutual respect',
      'Working independently',
      'Having the best leader'
    ],
    correctAnswer: 1,
    explanation: 'Effective teams have clear goals, open communication, mutual respect, defined roles, and accountability.',
    category: 'Teamwork',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'team_002',
    question: 'What is "Psychological Safety" in teams?',
    options: [
      'Physical safety at work',
      'Feeling safe to take risks and be vulnerable without fear',
      'Mental health insurance',
      'Security clearance'
    ],
    correctAnswer: 1,
    explanation: 'Psychological safety means team members feel safe to take interpersonal risks, share ideas, and admit mistakes without fear of embarrassment.',
    category: 'Teamwork',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'team_003',
    question: 'What is the best way to delegate tasks?',
    options: [
      'Give vague instructions',
      'Provide clear expectations, resources, and authority',
      'Do everything yourself',
      'Delegate only easy tasks'
    ],
    correctAnswer: 1,
    explanation: 'Effective delegation includes clear expectations, necessary resources, appropriate authority, and follow-up support.',
    category: 'Teamwork',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'team_004',
    question: 'What is "Groupthink"?',
    options: [
      'Thinking as a group',
      'When desire for harmony leads to poor decisions',
      'Brainstorming together',
      'Group meditation'
    ],
    correctAnswer: 1,
    explanation: 'Groupthink occurs when the desire for group harmony or conformity results in poor decision-making, suppressing dissenting viewpoints.',
    category: 'Teamwork',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'team_005',
    question: 'What is the purpose of a "Stand-up Meeting"?',
    options: [
      'To exercise while meeting',
      'A brief daily sync to share progress and blockers',
      'A formal presentation',
      'A meeting without chairs'
    ],
    correctAnswer: 1,
    explanation: 'Stand-up meetings are brief (15 min) daily syncs where team members share what they did, what they\'ll do, and any blockers.',
    category: 'Teamwork',
    difficulty: 'easy',
    points: 10
  },

  // ==================== TECHNOLOGY & TOOLS (15 questions) ====================
  {
    id: 'tech_001',
    question: 'What is the purpose of a task management system?',
    options: [
      'To make work more complicated',
      'To organize, track, and prioritize work',
      'To replace human workers',
      'To send emails automatically'
    ],
    correctAnswer: 1,
    explanation: 'Task management systems help organize, track, and prioritize work, ensuring nothing falls through the cracks and improving efficiency.',
    category: 'Tools',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'tech_002',
    question: 'What is "Cloud Storage"?',
    options: [
      'Storage in the sky',
      'Storing data on remote servers accessible via internet',
      'Storage on your computer',
      'Physical storage units'
    ],
    correctAnswer: 1,
    explanation: 'Cloud storage means storing data on remote servers accessed via the internet, enabling access from anywhere and automatic backups.',
    category: 'Tools',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'tech_003',
    question: 'What is "Automation"?',
    options: [
      'Working automatically',
      'Using technology to perform tasks without human intervention',
      'Automatic doors',
      'Self-driving cars'
    ],
    correctAnswer: 1,
    explanation: 'Automation uses technology to perform repetitive tasks without human intervention, freeing time for more valuable work.',
    category: 'Tools',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'tech_004',
    question: 'What is a "Keyboard Shortcut"?',
    options: [
      'A short keyboard',
      'Key combinations that perform actions quickly',
      'Typing faster',
      'A shortcut key on keyboard'
    ],
    correctAnswer: 1,
    explanation: 'Keyboard shortcuts are key combinations (like Ctrl+C for copy) that perform actions quickly, significantly boosting productivity.',
    category: 'Tools',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'tech_005',
    question: 'What is "Version Control"?',
    options: [
      'Controlling software versions',
      'A system for tracking changes to files over time',
      'Controlling your temper',
      'Managing different versions of products'
    ],
    correctAnswer: 1,
    explanation: 'Version control systems track changes to files over time, allowing you to recall specific versions and collaborate effectively.',
    category: 'Tools',
    difficulty: 'medium',
    points: 15
  },

  // ==================== LEADERSHIP & MANAGEMENT (15 questions) ====================
  {
    id: 'lead_001',
    question: 'What is the difference between a manager and a leader?',
    options: [
      'They are the same',
      'Managers control, leaders inspire',
      'Leaders are higher rank',
      'Managers work harder'
    ],
    correctAnswer: 1,
    explanation: 'Managers focus on processes and control, while leaders inspire, motivate, and create vision. Great managers are also leaders.',
    category: 'Leadership',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'lead_002',
    question: 'What is "Servant Leadership"?',
    options: [
      'Being a servant',
      'Leading by serving and supporting your team',
      'Following orders',
      'Doing everything yourself'
    ],
    correctAnswer: 1,
    explanation: 'Servant leadership prioritizes serving and supporting team members, helping them grow and perform at their best.',
    category: 'Leadership',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'lead_003',
    question: 'What is "Empowerment" in leadership?',
    options: [
      'Giving power to employees',
      'Giving authority, resources, and trust to make decisions',
      'Promoting everyone',
      'Letting people do whatever they want'
    ],
    correctAnswer: 1,
    explanation: 'Empowerment means giving employees the authority, resources, and trust to make decisions and take ownership of their work.',
    category: 'Leadership',
    difficulty: 'medium',
    points: 15
  },

  // ==================== PROBLEM SOLVING & DECISION MAKING (10 questions) ====================
  {
    id: 'prob_001',
    question: 'What is "Root Cause Analysis"?',
    options: [
      'Analyzing plant roots',
      'Identifying the fundamental reason for a problem',
      'Surface-level problem solving',
      'Blaming someone'
    ],
    correctAnswer: 1,
    explanation: 'Root cause analysis identifies the fundamental reason a problem occurred, rather than just addressing symptoms, preventing recurrence.',
    category: 'Problem Solving',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'prob_002',
    question: 'What is "Brainstorming"?',
    options: [
      'Thinking hard',
      'A group creativity technique to generate many ideas',
      'Individual thinking',
      'Storm of ideas'
    ],
    correctAnswer: 1,
    explanation: 'Brainstorming is a group technique where members freely share ideas without judgment, generating creative solutions to problems.',
    category: 'Problem Solving',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'prob_003',
    question: 'What is the "5 Whys" technique?',
    options: [
      'Asking why 5 times',
      'Asking "why" repeatedly to find root cause',
      'Five different questions',
      'A questioning method'
    ],
    correctAnswer: 1,
    explanation: 'The 5 Whys technique involves asking "why" repeatedly (typically 5 times) to drill down to the root cause of a problem.',
    category: 'Problem Solving',
    difficulty: 'medium',
    points: 15
  },

  // ==================== TIME MANAGEMENT (10 questions) ====================
  {
    id: 'time_001',
    question: 'What is "Parkinson\'s Law"?',
    options: [
      'A medical condition',
      'Work expands to fill the time available',
      'A time management app',
      'A productivity technique'
    ],
    correctAnswer: 1,
    explanation: 'Parkinson\'s Law states that work expands to fill the time available for its completion, suggesting shorter deadlines can increase efficiency.',
    category: 'Time Management',
    difficulty: 'hard',
    points: 20
  },
  {
    id: 'time_002',
    question: 'What is "Time Audit"?',
    options: [
      'Auditing financial time',
      'Tracking how you spend your time to identify patterns',
      'Checking clock accuracy',
      'Time zone calculations'
    ],
    correctAnswer: 1,
    explanation: 'A time audit involves tracking how you spend your time over a period to identify time-wasters and optimize your schedule.',
    category: 'Time Management',
    difficulty: 'easy',
    points: 10
  },
  {
    id: 'time_003',
    question: 'What is "Buffer Time"?',
    options: [
      'Time to buffer',
      'Extra time scheduled between tasks for transitions',
      'Break time',
      'Overtime'
    ],
    correctAnswer: 1,
    explanation: 'Buffer time is extra time scheduled between tasks or meetings to handle overruns, transitions, and unexpected issues.',
    category: 'Time Management',
    difficulty: 'easy',
    points: 10
  },

  // ==================== MOTIVATION & MINDSET (10 questions) ====================
  {
    id: 'motiv_001',
    question: 'What is "Growth Mindset"?',
    options: [
      'Wanting to grow taller',
      'Believing abilities can be developed through effort',
      'Growing your business',
      'Positive thinking'
    ],
    correctAnswer: 1,
    explanation: 'Growth mindset is the belief that abilities and intelligence can be developed through dedication, effort, and learning from failures.',
    category: 'Mindset',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'motiv_002',
    question: 'What is "Intrinsic Motivation"?',
    options: [
      'External rewards',
      'Internal drive from personal satisfaction',
      'Money motivation',
      'Fear of punishment'
    ],
    correctAnswer: 1,
    explanation: 'Intrinsic motivation comes from internal satisfaction and personal interest, rather than external rewards, leading to sustained engagement.',
    category: 'Mindset',
    difficulty: 'medium',
    points: 15
  },
  {
    id: 'motiv_003',
    question: 'What is "Imposter Syndrome"?',
    options: [
      'Being an imposter',
      'Doubting your abilities despite evidence of success',
      'Pretending to work',
      'Copying others'
    ],
    correctAnswer: 1,
    explanation: 'Imposter syndrome is doubting your abilities and feeling like a fraud despite evidence of competence and success.',
    category: 'Mindset',
    difficulty: 'hard',
    points: 20
  }
];

// Add more questions as needed - aim for 100+ total
// This ensures questions don't repeat for months!

